import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Button } from "../../../../../design-system";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { SectionRoot } from "../shared";
import { useContentEngine } from "./useContentEngine";
import {
  barChartData,
  highlightThreshold,
  mergedOutliers,
  nicheRanking,
  ownAccountStats,
  statStripData,
  topPosts,
} from "./homeDerive";
import StatStrip, { StatKey } from "./StatStrip";
import StatDetailPanel, { STAT_TITLES } from "./StatDetailPanel";
import ConnectionsStrip from "./ConnectionsStrip";
import Modal from "../Modal";
import PerformanceBars from "./PerformanceBars";
import TopPosts from "./TopPosts";
import PostDetailPanel from "./PostDetailPanel";
import NichePanel from "./NichePanel";
import AgentTeamRow from "./AgentTeamRow";

// The daily loop entry point, laid out as a pipeline dashboard: analytics up
// top, the niche research panel, and the content team cards that show what
// each stage of the weekly run last produced.

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const QuickActions = styled.div`
  display: flex;
  gap: var(--space-1);
  flex-wrap: wrap;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: var(--space-3);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SetupLine = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

export type HomeDashboardProps = {
  onNavigate: (path: string) => void;
};

export const HomeDashboard: React.FC<HomeDashboardProps> = ({ onNavigate }) => {
  const { community, communityId, posts, mediaConfig } = useMediaStudio();
  const engine = useContentEngine(communityId);

  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [selectedStat, setSelectedStat] = useState<StatKey | null>(null);

  const stats = useMemo(
    () => statStripData(posts.posts, engine.config),
    [posts.posts, engine.config]
  );
  const bars = useMemo(() => barChartData(posts.posts), [posts.posts]);
  const threshold = useMemo(
    () =>
      highlightThreshold(
        bars.map((b) => b.views),
        engine.config?.verdictThresholds?.winnerViews
      ),
    [bars, engine.config]
  );
  const top = useMemo(() => topPosts(posts.posts), [posts.posts]);
  const outliers = useMemo(() => mergedOutliers(engine.reports), [engine.reports]);
  const ranking = useMemo(() => {
    if (engine.baselines.length === 0) return [];
    const ownHandles = engine.config
      ? engine.config.accounts
          .map((a) => a.handle)
          .filter((h): h is string => typeof h === "string")
      : [];
    return nicheRanking(
      engine.baselines,
      outliers,
      ownAccountStats(posts.posts),
      ownHandles,
      community?.title || "You"
    );
  }, [engine.baselines, engine.config, outliers, posts.posts, community]);

  const selectedPost = selectedPostId
    ? posts.posts.find((p) => p.id === selectedPostId) || null
    : null;

  const needsBrandSetup = !mediaConfig.loading && !mediaConfig.hasAudienceData;

  return (
    <SectionRoot>
      <Header>
        <QuickActions>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("/media-studio/create?flow=slides")}
          >
            🖼 Slides
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("/media-studio/create?flow=viral")}
          >
            🎬 Viral reel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("/media-studio/create?flow=free")}
          >
            📹 Free reel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("/media-studio/create?flow=ads")}
          >
            📣 Ad
          </Button>
        </QuickActions>
      </Header>

      {needsBrandSetup && (
        <SetupLine>
          Set up your audience voice first. It feeds every generator.
          <Button
            variant="primary"
            size="sm"
            onClick={() => onNavigate("/media-studio/settings?section=brand")}
          >
            Set up brand voice
          </Button>
        </SetupLine>
      )}

      <ConnectionsStrip onNavigate={onNavigate} />

      <StatStrip
        data={stats}
        loading={posts.loading}
        selected={selectedStat}
        onSelect={(key) => setSelectedStat((s) => (s === key ? null : key))}
      />

      {selectedStat && (
        <Modal
          title={STAT_TITLES[selectedStat]}
          onClose={() => setSelectedStat(null)}
        >
          <StatDetailPanel
            statKey={selectedStat}
            onSelectPost={setSelectedPostId}
          />
        </Modal>
      )}

      <ChartsGrid>
        <PerformanceBars
          data={bars}
          threshold={threshold}
          selectedPostId={selectedPostId}
          onSelect={setSelectedPostId}
        />
        <TopPosts data={top} onSelect={setSelectedPostId} />
      </ChartsGrid>

      {selectedPost && (
        <Modal title="Post details" onClose={() => setSelectedPostId(null)}>
          <PostDetailPanel post={selectedPost} />
        </Modal>
      )}

      {(ranking.length > 0 || outliers.length > 0) && (
        <NichePanel
          reports={engine.reports}
          ranking={ranking}
          outliers={outliers}
        />
      )}

      <AgentTeamRow
        reports={engine.reports}
        concepts={engine.concepts}
        learning={engine.learning}
        expanded={expandedAgent}
        onExpand={setExpandedAgent}
        onNavigate={onNavigate}
        onSelectPost={setSelectedPostId}
      />
    </SectionRoot>
  );
};

export default HomeDashboard;
